import React, { useCallback, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import CleaningZone, { isCleaningZone, isTeachAndRepeatZone } from "../../../../interfaces/robot/rooms/cleaning-zone";
import RedZone from "../../../../interfaces/robot/rooms/red-zone";
import RuleZone, { isRuleZone } from "../../../../interfaces/robot/rooms/rule-zone";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { selectSelectedRoomZoneIdsAndNames } from "../../../../store/room/room-selectors";
import { validateZoneName } from "../../../../validation/zone-validation";
import ReadOnlyProtectedForm from "../../../Inputs/ReadOnlyProtectedForm";
import SelectedZoneFormCleaningZoneDetails from "./SelectedZoneFormCleaningZoneDetails";
import TextInput from "../../../Inputs/TextInput";
import useValidatedInputEffect from "../../../../hooks/useValidatedInputEffect";
import useServerValidationResponseEffect from "../../../../hooks/useServerValidationResponseEffect";
import {
  selectCleaningZonesRestSet,
  selectRedZonesRestSet,
  selectRuleZonesRestSet
} from "../../../../store/room/zone-planner/zone-planner-selectors";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import { useTranslation } from "react-i18next";
import SelectedZoneFormTeachAndRepeatZoneDetails from "./SelectedZoneFormTeachAndRepeatZoneDetails";
import SelectedZoneFormRuleZoneDetails from "./SelectedZoneFormRuleZoneDetails";

const SelectedZoneForm: React.FC<{
  zone: CleaningZone | RedZone | RuleZone;
}> = ({ zone }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { patchCleaningZonesRestSet, patchCleaningZone, patchRedZone, patchRedZonesRestSet, patchRuleZone, patchRuleZonesRestSet } = useSharedActions();

  const cleaningZoneRestState = useSharedSelector((state) => selectCleaningZonesRestSet(state));
  const redZoneRestState = useSharedSelector((state) => selectRedZonesRestSet(state));
  const ruleZoneRestState = useSharedSelector((state) => selectRuleZonesRestSet(state));
  const nonSelectedZoneIdsNames = useSharedSelector((state) => selectSelectedRoomZoneIdsAndNames(state)).filter(
    (z) => z.id !== zone.id
  );

  const isRuleZoneInstance = isRuleZone(zone);
  const isRedZone = !isRuleZoneInstance && !isCleaningZone(zone);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } = useServerValidationResponseEffect({
    patchError: isRuleZoneInstance ? ruleZoneRestState.patchError : isRedZone ? redZoneRestState.patchError : cleaningZoneRestState.patchError,
    resetPatchHandler: () =>
      isRuleZoneInstance
        ? dispatch(patchRuleZonesRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }))
        : 
        isRedZone
          ? dispatch(patchRedZonesRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }))
          : dispatch(patchCleaningZonesRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
  });

  const {
    value: zoneName,
    validationResult: zoneNameValidationResult,
    valueChangeHandler: zoneNameChangeHandler,
  } = useValidatedInputEffect(
    zone.name ?? "",
    (newName) => {
      const results = validateZoneName(newName, nonSelectedZoneIdsNames.filter((z) => z.name != null).map((z) => z.name!));
      return { isValid: results.length === 0, error: results.length > 0 ? results[0].error : null };
    },
    (_) => {},
    getServerValidationResultOrNull("name"),
    removeServerError,
    resetToggle
  );

  const patchZoneNameHandler = () => {
    if (zone.name !== zoneName && zoneNameValidationResult.isValid) {
      if (isRedZone) {
        dispatch(patchRedZone({ id: zone.id, data: { name: zoneName } }));
      } else if (isRuleZoneInstance) {
        dispatch(patchRuleZone({ id: zone.id, data: { name: zoneName } }));
      } else {
        dispatch(patchCleaningZone({ id: zone.id, data: { cleaningZone: { name: zoneName } } }));
      }
    }
  };

  const patchZoneNameHandlerRef = useRef(patchZoneNameHandler);
  patchZoneNameHandlerRef.current = patchZoneNameHandler; 

  useEffect(() => {
    return () => {
      patchZoneNameHandlerRef.current();
    };
  }, []);

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchZoneNameHandler();
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ReadOnlyProtectedForm noValidate autoComplete="off" onSubmit={formSubmitHandler}>
          <TextInput
            label={t("input-label-name")}
            onBlur={patchZoneNameHandler}
            value={zoneName}
            onChange={zoneNameChangeHandler}
            validationResult={zoneNameValidationResult}
          />
        </ReadOnlyProtectedForm>
      </Grid>
      {isCleaningZone(zone) && !isTeachAndRepeatZone(zone) && !isRuleZone(zone) && (
        <SelectedZoneFormCleaningZoneDetails zone={zone} />
      )}
      {isCleaningZone(zone) && isTeachAndRepeatZone(zone) && !isRuleZone(zone) && (
        <SelectedZoneFormTeachAndRepeatZoneDetails zone={zone} />
      )}
      {isRuleZone(zone) && <SelectedZoneFormRuleZoneDetails zone={zone} />}
    </Grid>
  );
};

export default SelectedZoneForm;
